import { translate, translateRich, useLocale } from "../localization";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import Filters from "../components/dashboard/Filters";
import DashboardTrendsChart from "../components/dashboard/DashboardTrendsChart";

import { useAnalytics } from "../context/AnalyticsContext";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useAuth } from "../context/AuthContext";
import ExperiencesApi from "../services/ExperiencesAPI";
import { getItem } from "../services/Content";

import "./PrototypePage.scss";

// Import visualization components
import ConceptMasteryHeatmap from "../components/prototype/ConceptMasteryHeatmap";
import EngagementScatter from "../components/prototype/EngagementScatter";
import TimeTrendChart from "../components/prototype/TimeTrendChart";
import LearningJourneySankey from "../components/prototype/LearningJourneySankey";
import EnvironmentTreemap from "../components/prototype/EnvironmentTreemap";
import ActivityStream from "../components/prototype/ActivityStream";
import CalendarHeatmap from "../components/prototype/CalendarHeatmap";
import ResponseQualityGauge from "../components/prototype/ResponseQualityGauge";

// NEW: Actionable analytics components
import QuestionsNeedingAttention from "../components/prototype/QuestionsNeedingAttention";
import EnvironmentPerformance from "../components/prototype/EnvironmentPerformance";
import StudentsToWatch from "../components/prototype/StudentsToWatch";
import WeekOverWeekComparison from "../components/prototype/WeekOverWeekComparison";

// NEW: Learning Journeys components
import CommonLearningPaths from "../components/prototype/CommonLearningPaths";
import EnvironmentEngagementTable from "../components/prototype/EnvironmentEngagementTable";
import ActivityByTime from "../components/prototype/ActivityByTime";

// NEW: Timeline visualization
import SessionTimeline from "../components/prototype/SessionTimeline";
import { AppButton } from "../components/ui";

export default function PrototypePage() {
  const locale = useLocale();
  const { setTitle, setShowTitleInHeader } = usePageMetadata();
  const { insights, sessions, statistics, refreshAnalytics } = useAnalytics();
  const { organizationId } = useAuth();
  const navigate = useNavigate();

  // Extended data state
  const [detailedSessions, setDetailedSessions] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [environmentData, setEnvironmentData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // overview, journey, engagement

  useEffect(() => {
    setShowTitleInHeader(true);
    setTitle(translate("Advanced Analytics Prototype"));
    refreshAnalytics().catch(() => {});
  }, [refreshAnalytics, setShowTitleInHeader, setTitle, locale]);

  // Fetch detailed session data using Experiences API
  useEffect(() => {
    if (!sessions || sessions.length === 0) return;

    const fetchDetailedData = async () => {
      setLoading(true);
      try {
        console.log("🔄 Loading session timeline data from Experiences API...");

        // Group sessions by experience ID to batch fetch assessments
        const sessionsByExperience = {};
        sessions.forEach(session => {
          const expId = session.experienceId;
          if (expId) {
            if (!sessionsByExperience[expId]) {
              sessionsByExperience[expId] = [];
            }
            sessionsByExperience[expId].push(session);
          }
        });

        const experienceIds = Object.keys(sessionsByExperience);
        console.log(`📊 Found ${experienceIds.length} experiences with sessions`);

        // Fetch assessments per experience (much more efficient than per-session)
        const allAssessmentsData = [];
        const BATCH_SIZE = 5;

        for (let i = 0; i < experienceIds.length; i += BATCH_SIZE) {
          const batch = experienceIds.slice(i, i + BATCH_SIZE);

          const batchResults = await Promise.all(
            batch.map(async (expId) => {
              try {
                const res = await ExperiencesApi.get(`assessments?experienceId=${expId}`, {
                  requiresAuth: true,
                });
                return { expId, data: res?.data || [] };
              } catch (err) {
                console.warn(`Failed to fetch assessments for experience ${expId}:`, err);
                return { expId, data: [] };
              }
            })
          );

          batchResults.forEach(({ data }) => {
            if (Array.isArray(data)) {
              allAssessmentsData.push(...data);
            }
          });
        }

        console.log(`✅ Loaded ${allAssessmentsData.length} assessments`);

        // Group assessments by session code
        const assessmentsBySession = {};
        allAssessmentsData.forEach(assessment => {
          const sessionCode = assessment.sessionCode;
          if (sessionCode) {
            if (!assessmentsBySession[sessionCode]) {
              assessmentsBySession[sessionCode] = [];
            }
            assessmentsBySession[sessionCode].push(assessment);
          }
        });

        // Build detailed sessions from assessments
        const allEvents = [];
        const envCache = {};

        sessions.slice(0, 50).forEach(session => {
          const sessionCode = session.sessionCode || session.code;
          const sessionAssessments = assessmentsBySession[sessionCode] || [];

          if (sessionAssessments.length > 0) {
            // Extract environment visits from assessments
            const environments = [...new Set(
              sessionAssessments
                .map(a => a.environmentId)
                .filter(Boolean)
            )];

            // Convert assessments to "events" format for timeline
            const events = sessionAssessments.map(a => ({
              pk: a.pk,
              sk: a.sk || a.timestamp,
              dateCreated: a.timestamp || a.sk,
              environmentId: a.environmentId,
              score: a.score,
              transcript: a.answer || a.transcript,
              summary: a.summary,
              actionType: a.actionType || translate("Assessment#Insight#{value0}", {
  value0: a.insightId
}),
            }));

            allEvents.push({
              sessionCode,
              events,
              environments,
              assessmentEvents: events,
            });

            // Collect environment IDs for name lookup
            environments.forEach(envId => {
              if (envId) envCache[envId] = null;
            });
          }
        });

        setDetailedSessions(allEvents);

        // Extract all assessments for other visualizations
        const formattedAssessments = allAssessmentsData.map(a => ({
          timestamp: a.sk || a.timestamp,
          score: Number(a.score ?? 0),
          answer: (a.answer || a.transcript || "").toString(),
          summary: a.summary || "",
          feedback: a.feedback || "",
          topics: a.topics || "",
          environmentId: a.environmentId,
          sessionCode: a.sessionCode,
          insightId: a.insightId,
        }));
        setAssessments(formattedAssessments);

        // Fetch environment names
        const uniqueEnvIds = Object.keys(envCache);
        console.log(`🌍 Fetching names for ${uniqueEnvIds.length} environments...`);

        for (const envId of uniqueEnvIds) {
          try {
            const env = await getItem("environments", envId, false);
            envCache[envId] = env?.name || translate("Environment {value0}", {
  value0: envId
});
          } catch {
            envCache[envId] = translate("Environment {value0}", {
  value0: envId
});
          }
        }
        setEnvironmentData(envCache);

        console.log("✅ Session timeline data loaded successfully!");

      } catch (error) {
        console.error("❌ Error fetching detailed data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedData();
  }, [sessions, locale]);

  // Helper to extract insight ID from actionType
  const extractInsightId = (actionType = "") => {
    const marker = translate("Insight#");
    const idx = actionType?.indexOf(marker);
    if (idx === -1) return null;
    return actionType.slice(idx + marker.length).trim() || null;
  };

  // Compute derived metrics
  const metrics = useMemo(() => {
    if (!sessions || !insights) return null;

    const avgSessionLength = sessions.reduce((sum, s) => 
      sum + (s.secondsSpent || 0), 0) / sessions.length;

    const completionRate = sessions.filter(s => 
      (s.responseCount || 0) > 0).length / sessions.length;

    const topEnvironment = Object.entries(
      detailedSessions.reduce((acc, s) => {
        s.environments.forEach(env => {
          acc[env] = (acc[env] || 0) + 1;
        });
        return acc;
      }, {})
    ).sort((a, b) => b[1] - a[1])[0];

    return {
      avgSessionLength: Math.round(avgSessionLength / 60), // minutes
      completionRate: Math.round(completionRate * 100),
      topEnvironment: topEnvironment 
        ? environmentData[topEnvironment[0]] || topEnvironment[0]
        : translate("Loading..."),
      totalResponses: assessments.length,
      activeStudents: sessions.length,
    };
  }, [sessions, insights, detailedSessions, environmentData, assessments, locale]);

  // Tab content renderer
  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <>
            {/* KPI Cards */}
            <div className="prototypeKpis">
              <div className="kpiCard">
                <div className="kpiLabel">{translate("AVG SESSION LENGTH")}</div>
                <div className="kpiValue">{translateRich("{value0} min", {
  value0: metrics?.avgSessionLength || 0
})}</div>
                <div className="kpiSub">{translate("Optimal engagement window")}</div>
              </div>
              <div className="kpiCard">
                <div className="kpiLabel">{translate("COMPLETION RATE")}</div>
                <div className="kpiValue">{metrics?.completionRate || 0}%</div>
                <div className="kpiSub">{translate("Students who responded")}</div>
              </div>
              <div className="kpiCard">
                <div className="kpiLabel">{translate("TOP ENVIRONMENT")}</div>
                <div className="kpiValue kpiValue--small">{metrics?.topEnvironment || "—"}</div>
                <div className="kpiSub">{translate("Most visited space")}</div>
              </div>
              <div className="kpiCard">
                <div className="kpiLabel">{translate("TOTAL RESPONSES")}</div>
                <div className="kpiValue">{metrics?.totalResponses || 0}</div>
                <div className="kpiSub">{translate("Across all insights")}</div>
              </div>
            </div>

            {/* Two-column: Questions Needing Attention + Students to Watch */}
            <div className="prototypeGrid twoColumn">
              <div className="prototypeCard">
                <h4>{translate("\uD83C\uDFAF Questions Needing Attention")}</h4>
                <p className="cardDescription">{translate("Lowest scoring questions \u2014 review content or provide additional support")}</p>
                <QuestionsNeedingAttention 
                  insights={insights || []}
                  environmentData={environmentData}
                  limit={5}
                />
              </div>
              <div className="prototypeCard">
                <h4>{translate("\u26A0\uFE0F Students to Watch")}</h4>
                <p className="cardDescription">{translate("Students who may need intervention or follow-up")}</p>
                <StudentsToWatch 
                  sessions={sessions || []}
                  limit={5}
                  scoreThreshold={40}
                />
              </div>
            </div>

            {/* Environment Performance - Full Width */}
            <div className="prototypeSection">
              <h3>{translate("\uD83D\uDCCA Environment Performance")}</h3>
              <p className="sectionDescription">{translate("How students perform across different learning environments")}</p>
              <EnvironmentPerformance 
                insights={insights || []}
                environmentData={environmentData}
                limit={6}
              />
            </div>

            {/* Week Over Week Comparison */}
            <div className="prototypeSection">
              <h3>{translate("\uD83D\uDCC5 This Week vs Last Week")}</h3>
              <p className="sectionDescription">{translate("Quick comparison to track progress over time")}</p>
              <WeekOverWeekComparison sessions={sessions || []} />
            </div>
          </>
        );

      case "journey":
        return (
          <>
            {/* Common Learning Paths - Full Width */}
            <div className="prototypeSection">
              <h3>{translate("\uD83D\uDEE4\uFE0F Common Learning Paths")}</h3>
              <p className="sectionDescription">{translate("Most frequent sequences students take through your environments")}</p>
              <CommonLearningPaths 
                detailedSessions={detailedSessions}
                environmentData={environmentData}
                limit={6}
              />
            </div>

            {/* Environment Engagement Table - Full Width */}
            <div className="prototypeSection">
              <h3>{translate("\uD83D\uDCCA Environment Engagement")}</h3>
              <p className="sectionDescription">{translate("Detailed metrics for each environment \u2014 click column headers to sort")}</p>
              <EnvironmentEngagementTable 
                detailedSessions={detailedSessions}
                insights={insights || []}
                environmentData={environmentData}
                limit={10}
              />
            </div>

            {/* Activity by Time */}
            <div className="prototypeSection">
              <h3>{translate("\uD83D\uDD50 Activity Patterns")}</h3>
              <p className="sectionDescription">{translate("When are your students most active?")}</p>
              <ActivityByTime 
                sessions={sessions || []}
                detailedSessions={detailedSessions}
              />
            </div>
          </>
        );

      case "engagement":
        return (
          <>
            <div className="prototypeGrid twoColumn">
              <div className="prototypeCard tall">
                <h4>{translate("Live Activity Stream")}</h4>
                <ActivityStream sessions={sessions || []} limit={20} />
              </div>
              <div className="prototypeCard tall">
                <h4>{translate("Response Quality Distribution")}</h4>
                <ResponseQualityGauge assessments={assessments} detailed />
                <div className="prototypeKpiRow">
                  <div className="prototypeKpiCard">
                    <h4>{translate("Avg Response Length")}</h4>
                    <div className="statDisplay compact">{translateRich("{value0} chars", {
  value0: Math.round(assessments.reduce((sum, a) => sum + (a.answer?.length || 0), 0) / (assessments.length || 1))
})}</div>
                  </div>
                  <div className="prototypeKpiCard">
                    <h4>{translate("Student Response Rate")}</h4>
                    <div className="statDisplay compact">
                      {Math.round(
                        (assessments.filter((a) => (a.answer || "").trim()).length /
                          (assessments.length || 1)) *
                          100
                      )}
                      %
                    </div>
                  </div>
                  <div className="prototypeKpiCard">
                    <h4>{translate("Topics Extracted")}</h4>
                    <div className="statDisplay compact">
                      {[
                        ...new Set(
                          assessments.flatMap((a) =>
                            (a.topics || "")
                              .split(/[,|]/)
                              .filter(Boolean)
                          )
                        ),
                      ].length}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="prototypeSection">
              <h3>{translate("Session Length Distribution")}</h3>
              <EngagementScatter 
                sessions={sessions || []}
                onSessionClick={(sessionCode) => navigate(`/sessions/${sessionCode}`)}
              />
            </div>
          </>
        );

      case "timeline":
        return (
          <>
            {/* Session Timeline - Full Width */}
            <div className="prototypeSection">
              <h3>{translate("\uD83D\uDCCA Session Timeline")}</h3>
              <p className="sectionDescription">{translate("Gantt-style view of how students move through environments during their sessions. Each row is a session, colored segments show environment visits over time.")}</p>
              <SessionTimeline 
                detailedSessions={detailedSessions}
                sessions={sessions || []}
                environmentData={environmentData}
                maxSessions={40}
              />
            </div>

            {/* Tips */}
            <div className="prototypeSection">
              <div className="prototypeTips">
                <h4>{translate("\uD83D\uDCA1 How to Read This Chart")}</h4>
                <ul>
                  <li>{translateRich("{value0} represents one student session", {
  value0: <strong>{translate("Each row")}</strong>
})}</li>
                  <li>{translateRich("{value0} show which environment was being visited", {
  value0: <strong>{translate("Colored bars")}</strong>
})}</li>
                  <li>{translateRich("{value0} shows when in the session (left = start, right = end)", {
  value0: <strong>{translate("Bar position")}</strong>
})}</li>
                  <li>{translateRich("{value0} shows how long they spent in that environment", {
  value0: <strong>{translate("Bar length")}</strong>
})}</li>
                  <li>{translateRich("{value0} on any environment in the legend to filter", {
  value0: <strong>{translate("Click")}</strong>
})}</li>
                  <li>{translateRich("{value0} over segments for exact timing details", {
  value0: <strong>{translate("Hover")}</strong>
})}</li>
                  <li>{translateRich("{value0} on a row to view that session's details", {
  value0: <strong>{translate("Click")}</strong>
})}</li>
                </ul>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="prototypePage">
      <div className="prototypeContent">
        {/* Header */}
        <div className="prototypeTopRow">
          <Link to="/dashboard" className="prototypeBack">{translate("\u2190 Back to Dashboard")}</Link>
          <div className="prototypeTag">{translate("ADVANCED ANALYTICS")}</div>
        </div>

        {/* Filters */}
        <Filters />

        {/* Tab Navigation */}
        <div className="prototypeTabs">
          <AppButton
            type="button"
            kind={activeTab === "overview" ? "primary" : "ghost"}
            className={`tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >{translate("Overview")}</AppButton>
          <AppButton
            type="button"
            kind={activeTab === "journey" ? "primary" : "ghost"}
            className={`tab ${activeTab === "journey" ? "active" : ""}`}
            onClick={() => setActiveTab("journey")}
          >{translate("Learning Journeys")}</AppButton>
          <AppButton
            type="button"
            kind={activeTab === "engagement" ? "primary" : "ghost"}
            className={`tab ${activeTab === "engagement" ? "active" : ""}`}
            onClick={() => setActiveTab("engagement")}
          >{translate("Engagement")}</AppButton>
          <AppButton
            type="button"
            kind={activeTab === "timeline" ? "primary" : "ghost"}
            className={`tab ${activeTab === "timeline" ? "active" : ""}`}
            onClick={() => setActiveTab("timeline")}
          >{translate("Session Timeline")}</AppButton>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="prototypeLoading">
            <div className="spinner" />
            <p>{translate("Loading detailed analytics data...")}</p>
          </div>
        )}

        {/* Content */}
        {!loading && renderTabContent()}

        {/* Debug Info (remove in production) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="prototypeDebug">
            <details>
              <summary>{translate("Debug Info")}</summary>
              <pre>{translateRich("Sessions: {value0}Insights: {value1}Detailed Sessions: {value2}Assessments: {value3}Environments: {value4}", {
  value0: sessions?.length || 0,
  value1: insights?.length || 0,
  value2: detailedSessions.length,
  value3: assessments.length,
  value4: Object.keys(environmentData).length
})}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
